<template>
  <section class="all-checks" aria-labelledby="all-checks-title">
    <div class="all-checks-head">
      <h2 id="all-checks-title" class="all-checks-title">All {{ counts.total }} checks</h2>
      <p class="all-checks-counts">{{ counts.toFix }} to fix · {{ counts.passed }} passed · {{ counts.tips }} tips</p>
      <button
        type="button"
        class="all-checks-toggle no-print"
        :aria-pressed="improvementsOnly ? 'true' : 'false'"
        @click="$emit('toggle-improvements')"
      >
        <span class="all-checks-switch" aria-hidden="true" />
        Show improvements only
      </button>
    </div>

    <p v-if="!columns.flat().length" class="all-checks-empty">Nothing to improve. Every scored check passed.</p>

    <div class="all-checks-columns">
      <div v-for="(column, index) in columns" :key="index" class="all-checks-column">
        <section v-for="category in column" :key="category.name" class="category-card" :aria-labelledby="`category-${category.id}`">
          <div class="category-card-head">
            <ph-icon :name="category.icon" :size="22" class="category-card-icon" />
            <h3 :id="`category-${category.id}`" class="category-card-name">{{ category.label }}</h3>
            <span
              v-if="category.score !== null"
              class="category-card-badge"
              :style="{ background: `var(--ex-score-${category.level})` }"
              :aria-label="`${category.score} out of 10`"
            >{{ category.score }}</span>
            <span v-else class="category-card-tips">Tips only</span>
          </div>
          <p v-if="category.description" class="category-card-description">{{ category.description }}</p>
          <ul class="category-card-checks">
            <li v-for="check in category.checks" :key="check.id || check.name" class="check-row">
              <check-status :check="check" variant="icon" class="check-row-icon" />
              <div class="check-row-body">
                <p class="check-row-name" :class="{ 'check-row-name--tip': !isScored(check) }">{{ check.name }}</p>
                <div v-if="check.description" class="check-row-description" v-html="sanitise(check.description)" />
              </div>
              <check-status :check="check" variant="label" class="check-row-result" />
            </li>
          </ul>
        </section>
      </div>
    </div>
  </section>
</template>

<script>
import CheckStatus from './CheckStatus.vue'
import PhIcon from './PhIcon.vue'
import sanitiseHtml from '../score/sanitiseHtml'
import { CATEGORY_COLUMNS, formatScore, getCategoryLabel, getCategoryMeta, getScoreLevel, hasCheckScore, isScoredCategory, sortChecks } from '../score/report.mjs'

export default {
  name: 'AllChecks',
  components: { CheckStatus, PhIcon },
  props: {
    // The categories to show, already filtered when improvements only is on.
    details: { type: Object, required: true },
    // Counts over the whole result, from countChecks().
    counts: { type: Object, required: true },
    improvementsOnly: { type: Boolean, default: false }
  },
  computed: {
    categories () {
      return Object.entries(this.details).map(([name, group]) => {
        const meta = getCategoryMeta(name)
        const scored = isScoredCategory(group)
        return {
          name,
          id: name.toLowerCase().replace(/[^a-z]+/g, '-'),
          label: getCategoryLabel(name),
          icon: meta.icon,
          description: group.description || meta.description,
          score: scored ? formatScore(group.score) : null,
          level: scored ? getScoreLevel(group.score) : null,
          checks: sortChecks(group.checks ?? [])
        }
      })
    },
    // The fixed two-column split; categories it doesn't know go to the shorter column.
    columns () {
      const columns = CATEGORY_COLUMNS.map((names) => names
        .map((name) => this.categories.find((category) => category.name === name))
        .filter(Boolean))
      for (const category of this.categories) {
        if (CATEGORY_COLUMNS.flat().includes(category.name)) continue
        const size = (column) => column.reduce((total, item) => total + item.checks.length + 2, 0)
        const shortest = size(columns[0]) <= size(columns[1]) ? columns[0] : columns[1]
        shortest.push(category)
      }
      return columns
    }
  },
  methods: {
    isScored: hasCheckScore,
    sanitise: sanitiseHtml
  }
}
</script>

<style scoped>
.all-checks-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 20px;
  margin-bottom: 20px;
}

.all-checks-title {
  font-size: 28px;
  letter-spacing: -0.02em;
}

.all-checks-counts {
  font-size: 14px;
  color: var(--ex-color-neutral-500);
}

.all-checks-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  padding: 4px 0;
  border: 0;
  background: none;
  color: var(--ex-color-neutral-700);
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}

.all-checks-switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-color-neutral-100);
}

.all-checks-switch::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-color-white);
}

.all-checks-toggle[aria-pressed='true'] .all-checks-switch {
  background: var(--ex-color-blue);
}

.all-checks-toggle[aria-pressed='true'] .all-checks-switch::after {
  left: 18px;
}

@media (prefers-reduced-motion: no-preference) {
  .all-checks-switch,
  .all-checks-switch::after {
    transition: all 160ms ease;
  }
}

.all-checks-empty {
  padding: 24px;
  border: 1px solid var(--ex-color-hairline);
  border-radius: var(--ex-radius-panel);
  background: var(--ex-color-white);
  color: var(--ex-color-neutral-700);
}

.all-checks-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: start;
}

.all-checks-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.category-card {
  padding: 22px 24px 8px;
  border: 1px solid var(--ex-color-hairline);
  border-radius: var(--ex-radius-panel);
  background: var(--ex-color-white);
}

.category-card-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 6px;
}

.category-card-icon {
  color: var(--ex-color-blue);
}

.category-card-name {
  flex: 1;
  font-size: 19px;
  letter-spacing: -0.02em;
}

.category-card-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex: none;
  border-radius: var(--ex-radius-pill);
  color: var(--ex-color-white);
  font-family: var(--ex-font-heading);
  font-size: 13px;
  font-weight: 600;
}

.category-card-tips {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-color-surface-blue);
  color: var(--ex-color-neutral-500);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.category-card-description {
  padding-bottom: 12px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--ex-color-neutral-500);
}

.category-card-checks {
  margin: 0;
  padding: 0;
  list-style: none;
}

.check-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 0;
  border-top: 1px solid var(--ex-color-hairline);
}

.check-row-icon {
  margin-top: 2px;
}

.check-row-body {
  flex: 1;
  min-width: 0;
}

.check-row-name {
  font-size: 14px;
  font-weight: 500;
}

.check-row-name--tip {
  font-weight: 400;
  color: var(--ex-color-neutral-700);
}

.check-row-description {
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--ex-color-neutral-500);
  overflow-wrap: anywhere;
}

.check-row-description ::v-deep p {
  margin: 0;
}

.check-row-description ::v-deep code {
  color: var(--ex-color-blue);
  background: var(--ex-color-blue-light);
}

@media (max-width: 960px) {
  .all-checks-columns {
    grid-template-columns: 1fr;
  }

  .all-checks-toggle {
    margin-left: 0;
  }
}

@media print {
  .category-card {
    break-inside: avoid;
  }
}
</style>
